/**
 * The rules, as tables that mirror docs/rules.md. Each table names the section
 * it comes from; masses are in grams and distances in metres.
 */
import type {
  Aircraft,
  FlightCondition,
  FlightPlan,
  PilotInput,
  RequirementId,
  SubcategoryId,
} from "../types";

/** Everything a subcategory requirement can look at. */
export interface Context {
  aircraft: Aircraft;
  pilot: PilotInput;
  flight: FlightPlan;
}

export interface Requirement {
  id: RequirementId;
  /** For distance requirements: the minimum that applies, and the planned figure. */
  distance?: { minimum: (context: Context) => number; actual: (flight: FlightPlan) => number };
  met: (context: Context) => boolean;
}

export interface SubcategoryRule {
  id: SubcategoryId;
  /** The column heading in the subcategory matrix. */
  column: string;
  aircraft: (aircraft: Aircraft) => boolean;
  requires: Requirement[];
}

export interface RegistrationRow {
  /** The aircraft cell in the registration table. */
  row: string;
  applies: (aircraft: Aircraft) => boolean;
  flyerId: "required" | "recommended";
  operatorId: "required" | "optional";
}

/** When a rule applies, inclusive, as ISO dates (which compare lexically). */
export interface Dated {
  from?: string;
  until?: string;
}

export const inForce = ({ from, until }: Dated, today: string): boolean =>
  (from === undefined || today >= from) && (until === undefined || today <= until);

export interface RemoteIdRow extends Dated {
  /** The class mark cell in the Remote ID table. */
  row: string;
  applies: (aircraft: Aircraft) => boolean;
  /** The row allows an exemption, such as for UK4 model aircraft. */
  exemptable?: boolean;
}

/** Aircraft at or above this mass are outside the Open category. */
export const OPEN_CATEGORY_BELOW_GRAMS = 25_000;

/** docs/rules.md § Class marks and Remote ID: C-class aircraft fly as their UK class. */
export const classEquivalence: Dated = { until: "2027-12-31" };

/** docs/rules.md § Registration and competency. The first matching row applies. */
export const registration: RegistrationRow[] = [
  {
    row: "250g to under 25kg (UK1 to UK4)",
    applies: ({ mass }) => mass >= 250,
    flyerId: "required",
    operatorId: "required",
  },
  {
    row: "100g to under 250g, with a camera (UK0)",
    applies: ({ mass, camera }) => mass >= 100 && camera,
    flyerId: "required",
    operatorId: "required",
  },
  {
    row: "100g to under 250g, no camera (UK0)",
    applies: ({ mass }) => mass >= 100,
    flyerId: "required",
    operatorId: "optional",
  },
  {
    row: "Under 100g",
    applies: () => true,
    flyerId: "recommended",
    operatorId: "optional",
  },
];

const when = (id: RequirementId, met: Requirement["met"]): Requirement => ({ id, met });

const atLeast = (
  id: RequirementId,
  actual: (flight: FlightPlan) => number,
  minimum: number | ((context: Context) => number),
): Requirement => {
  const minimumFor = typeof minimum === "number" ? () => minimum : minimum;
  return {
    id,
    distance: { minimum: minimumFor, actual },
    met: (context) => actual(context.flight) >= minimumFor(context),
  };
};

const notOverCrowd = when("notOverCrowd", ({ flight }) => !flight.crowd);
const notOverPeople = when("notOverPeople", ({ flight }) => !flight.overPeople);

/**
 * docs/rules.md § Subcategory matrix, least restrictive first. A flight fits
 * the first subcategory whose aircraft test passes and whose requirements are
 * all met. UK5, UK6 and expired EU marks match no row.
 */
export const subcategories: SubcategoryRule[] = [
  {
    id: "A1",
    column: "A1: Over People",
    aircraft: ({ ukClass, byWeight, mass }) =>
      ukClass === "UK0" || ukClass === "UK1" || (byWeight && mass < 250),
    requires: [notOverCrowd],
  },
  {
    id: "A2",
    column: "A2: Near People",
    aircraft: ({ ukClass, byWeight, mass }) =>
      (byWeight && mass < 2_000) || (ukClass === "UK2" && mass < 4_000),
    requires: [
      notOverCrowd,
      when("a2cofc", ({ pilot }) => pilot.a2cofc),
      notOverPeople,
      atLeast(
        "peopleDistance",
        (flight) => flight.peopleDistance,
        ({ aircraft, flight }) => (aircraft.ukClass !== "UK2" ? 50 : flight.lowSpeed ? 5 : 30),
      ),
    ],
  },
  {
    id: "A3",
    column: "A3: Far from People",
    aircraft: ({ ukClass, byWeight }) =>
      byWeight || ukClass === "UK2" || ukClass === "UK3" || ukClass === "UK4",
    requires: [
      notOverCrowd,
      notOverPeople,
      atLeast("peopleDistance", (flight) => flight.peopleDistance, 50),
      atLeast("areaDistance", (flight) => flight.builtDistance, 150),
      atLeast("buildingDistance", (flight) => flight.buildingDistance, 50),
    ],
  },
];

const withCamera = ({ mass, camera }: Aircraft) => mass >= 100 && camera;

/**
 * docs/rules.md § Class marks and Remote ID, one row per table row. Remote ID
 * applies when any matching row is in force.
 */
export const remoteId: RemoteIdRow[] = [
  {
    row: "UK0 (C0)",
    applies: (aircraft) => aircraft.ukClass === "UK0" && withCamera(aircraft),
    from: "2028-01-01",
  },
  { row: "UK1 (C1)", applies: ({ ukClass }) => ukClass === "UK1", from: "2026-01-01" },
  { row: "UK2 (C2)", applies: ({ ukClass }) => ukClass === "UK2", from: "2026-01-01" },
  { row: "UK3 (C3)", applies: ({ ukClass }) => ukClass === "UK3", from: "2026-01-01" },
  {
    row: "UK4 (C4), model aircraft",
    applies: ({ ukClass }) => ukClass === "UK4",
    from: "2028-01-01",
    exemptable: true,
  },
  {
    row: "No class mark (legacy)",
    applies: (aircraft) => aircraft.origin === "unmarked" && withCamera(aircraft),
    from: "2028-01-01",
  },
  {
    row: "Privately built",
    applies: (aircraft) => aircraft.origin === "privatelyBuilt" && withCamera(aircraft),
    from: "2028-01-01",
  },
  {
    // Stated below the table rather than in it.
    row: "UK5 and UK6",
    applies: ({ ukClass }) => ukClass === "UK5" || ukClass === "UK6",
    from: "2026-01-01",
  },
];

/** docs/rules.md § Conditions for every flight, one entry per table row. */
export const conditions: FlightCondition[] = [
  {
    row: "Height",
    key: "height",
    title: "Height",
    text: "Stay below 120m (400ft).",
    citation: "whereYouCanFly",
  },
  {
    row: "Line of sight",
    key: "lineOfSight",
    title: "Line of sight",
    text: "Keep the aircraft in direct sight at all times, with a full view of the surrounding airspace.",
    citation: "whereYouCanFly",
  },
  {
    row: "Restricted airspace",
    key: "airspace",
    title: "Restricted airspace",
    text: "Do not fly in restricted airspace. The map covers permanent restrictions only: check NOTAMs, AIP Supplements and briefing sheets for temporary ones before every flight.",
    citation: "restrictionZones",
  },
  {
    row: "Dangerous goods and dropping articles",
    key: "dropping",
    title: "Dangerous goods",
    text: "Do not carry dangerous goods or drop articles.",
    citation: "whereYouCanFly",
  },
  {
    row: "Privacy",
    key: "privacy",
    title: "Privacy",
    text: "Respect people's privacy and do not cause a nuisance.",
    citation: "whereYouCanFly",
  },
  {
    row: "Take-off and landing",
    key: "landowner",
    title: "Take-off and landing",
    text: "The owner of the land or property must allow take-off and landing.",
    citation: "whereYouCanFly",
  },
  {
    row: "Insurance",
    key: "insurance",
    title: "Insurance",
    text: "Insurance is optional for recreation or sport. Commercial flying needs third-party insurance and is outside this tool.",
    citation: "whereYouCanFly",
  },
  {
    row: "Penalties",
    key: "penalties",
    title: "Penalties",
    text: "Flying without the required IDs is against the law. Fines and, in the most serious cases, prison are possible.",
    citation: "whereYouCanFly",
  },
];

/** The "Night flying" row: a green flashing light, evaluated only for night flights. */
export const nightLight: Dated & { row: string } = { row: "Night flying", from: "2026-01-01" };

export interface RestrictionRow {
  /** The restriction cell in the restricted-airspace table. */
  row: string;
  /** Matches a zone's type or designator from the airspace dataset. */
  matches: (zoneType: string) => boolean;
  permission: string;
}

/**
 * docs/rules.md § Restricted airspace, for permanent zones. The first matching
 * row applies; the last matches any zone.
 */
export const restrictions: RestrictionRow[] = [
  {
    row: "Aerodrome Flight Restriction Zone (FRZ) and Runway Protection Zones (RPZ)",
    matches: (zoneType) => /\b(frz|rpz)\b|aerodrome|runway/i.test(zoneType),
    permission:
      "Get permission from air traffic control, or from the airport if air traffic control is not operating.",
  },
  {
    row: "Ministry of Defence Prohibited Places",
    matches: (zoneType) => /prohibited|\bmod\b/i.test(zoneType),
    permission: "Request to operate through the CAA ACOMS notification form.",
  },
  {
    row: "Other permanent areas",
    matches: () => true,
    permission: "See UK AIP ENR 5.1 for who can give permission.",
  },
];

/** The "Temporary restrictions" row: not in the dataset, so always a reminder. */
export const temporaryRestrictions = {
  row: "Temporary restrictions",
  reminder: "Check NOTAMs, AIP Supplements and briefing sheets for temporary restrictions.",
};
